import React, { lazy, Suspense } from 'react'; // Force redeploy
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { NotificationProvider } from './Contexts/NotificationContext';
import { AuthProvider, useAuth } from './Contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsentBanner from './components/CookieConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './Pages/NotFound';
import Spinner from './components/ui/Spinner';

// Legal Pages
import { PrivacyPolicy, TermsAndConditions, CookiePolicy, RefundPolicy } from './Pages/Legal';

// Support Pages
import HelpCenter from './Pages/Support/HelpCenter';

// Pages
import SubscriptionPlans from './Pages/Subscriptions';
import Agencies from './Pages/Agencies';
import ContentCreators from './Pages/ContentCreators';
import Login from './Pages/Login/Login';
import ForgotPassword from './Pages/Login/ForgotPassword';
import ResetPassword from './Pages/Login/ResetPassword';
import PreviewGate from './components/PreviewGate';
import AuthCallback from './Pages/AuthCallback';
import MarketingServices from './Pages/MarketingServices/MarketingServices';
import AiTools from './Pages/Aitools';
import Checkout from './Pages/Checkout';
import BlogArticleGenerator from './Tools/BlogArticleGenerator';
import PromotionalEmailGenerator from './Tools/PromotionalEmailGenerator';
import CorporateProducts from './Pages/CorporateProducts';
import MarketingGenius from './Pages/MarketingGenius';
import PaidAds from './Pages/MarketingServices/Paidads';
import Socials from './Pages/MarketingServices/Socials';
import Websites from './Pages/MarketingServices/Websites';
import Wix from './Pages/MarketingServices/Websites/Wix';
import Shopify from './Pages/MarketingServices/Websites/Shopify';
import Shopify_p from './Pages/Platforms/Shopify_p';
import Videos from './Pages/MarketingServices/Videos';
import Pr from './Pages/MarketingServices/Pr';
import MoreMS from './Pages/MarketingServices/MoreMS';
import ProductDetail from './Pages/MarketingServices/ProductDetail';
import Affiliate from './Pages/Affiliate';
// Platform Pages
import Google from './Pages/Platforms/Google';
import Youtube from './Pages/Platforms/Youtube';
import Facebook from './Pages/Platforms/Facebook';
import Wordpress from './Pages/Platforms/Wordpress';
import MoreplatformOthers from './Pages/Platforms/MoreplatformOthers';
import Instagram from './Pages/Platforms/Instagram';
import TikTok from './Pages/Platforms/TikTok';
import XPlatform from './Pages/Platforms/X';
import LinkedIn from './Pages/Platforms/LinkedIn';
import Pinterest from './Pages/Platforms/Pinterest';
import Snapchat from './Pages/Platforms/Snapchat';
import Spotify from './Pages/Platforms/Spotify';
import Amazon from './Pages/Platforms/Amazon';
import ShopifyPlatform from './Pages/Platforms/Shopify';
import WixPlatform from './Pages/Platforms/Wix';
import Webflow from './Pages/Platforms/Webflow';
import Reddit from './Pages/Platforms/Reddit';
import Telegram from './Pages/Platforms/Telegram';

import Homepage from "./Pages/Homepage/Homepage";
import BlogPage from "./Pages/Blog/BlogPage";
import BlogDetail from "./Pages/Blog/BlogDetail";
import Contact from './Pages/Contact';
import Aboutus from './Pages/Aboutus';
import Discover from './Pages/Discover';
import ShopByIndustry from './Pages/ShopByIndustry';
import Resources from './Pages/Resources';
import CampaignSimulatorDocs from './Pages/TechnicalDocs/CampaignSimulatorDocs';

// Shared
import Header from './Shared/Header'
import CallToAction from "./Pages/Homepage/CallToAction";
import Footer from "./Shared/Footer";
import ScrollToTop from './Shared/ScrollToTop';

import ProductTest from "./Pages/ProductTest";

// Stylesheets
import './Assets/Stylesheets/Style.scss'

// Import components
import SocialmedpostGenerator from './Tools/SocialmedpostGenerator';
import DigitaladGenerator from './Tools/DigitaladGenerator';
import LogoGenerator from './Tools/LogoGenerator';
import MoreTools from './Tools/MoreTools';

// Lazy-loaded heavy, auth-gated portals — kept out of the initial bundle.
const Dashboard = lazy(() => import('./Pages/Portals/Dashboard'));
const AdminPortal = lazy(() => import('./Pages/Portals/AdminPortal'));
const VendorPortal = lazy(() => import('./Pages/Portals/VendorPortal'));
const CustomerPortal = lazy(() => import('./Pages/Portals/CustomerPortal'));

// Component to conditionally render footer and call-to-action
function ConditionalFooter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isPortalPage = location.pathname.includes('-portal');
  const isMarketingGenius = location.pathname === '/marketing-genius';
  
  // Hide footer for portal pages or Marketing Genius when user is signed in
  if (isPortalPage || (isMarketingGenius && user && !loading)) {
    return null; // Don't render footer/call-to-action on portal pages or Marketing Genius when signed in
  }
  
  return (
    <>
      <CallToAction/>
      <Footer />
    </>
  );
}

function App() {

  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <ScrollToTop />
          <ErrorBoundary>
          <div className="App">
            <Header/>
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={36} /></div>}>
          <Routes>
          <Route
            path="/"
            element={<Homepage/>}
          />
          <Route path="/discover" element={<Discover/>}/>
          <Route path="/shop-by-industry" element={<ShopByIndustry/>}/>
          <Route path="/resources" element={<Resources/>}/>
          <Route path="/resources/technical-docs/campaign-simulator" element={<CampaignSimulatorDocs/>}/>
          <Route path="/subscriptions-plans" element={<SubscriptionPlans/>}/>
          <Route path="/agencies" element={<Agencies/>}/>
          <Route path="/content-creators" element={<ContentCreators/>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path="/forgot-password" element={<ForgotPassword/>}/>
          <Route path="/reset-password" element={<ResetPassword/>}/>
          <Route path="/auth/callback" element={<AuthCallback/>}/>
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminPortal/>
            </ProtectedRoute>
          }/>
          <Route path="/vendor-portal" element={
            <ProtectedRoute requiredRole="vendor">
              <PreviewGate title="Vendor portal is coming soon">
                <VendorPortal/>
              </PreviewGate>
            </ProtectedRoute>
          }/>
          <Route path="/customer-portal" element={
            <ProtectedRoute requiredRole="customer">
              <PreviewGate title="Customer portal is coming soon">
                <CustomerPortal/>
              </PreviewGate>
            </ProtectedRoute>
          }/>
          <Route path="/marketing-services" element={<MarketingServices/>}/>
          <Route path="/ai-marketing-tools" element={<AiTools/>}/>
          <Route path="/blog-article-generator" element={<BlogArticleGenerator/>}/>
          <Route path="/promotional-email-generator" element={<PromotionalEmailGenerator/>}/>
          <Route path="/corporate-products" element={<CorporateProducts/>}/>
          <Route path="/marketing-genius" element={<MarketingGenius/>}/>
          <Route path="/paid-ads" element={<PaidAds/>}/>
          <Route path="/socials" element={<Socials/>}/>
          <Route path="/websites" element={<Websites/>}/>
          <Route path="/wix" element={<Wix/>}/>
          <Route path="/shopify" element={<Shopify/>}/>
          <Route path="/platforms/shopify_p" element={<Shopify_p />} />
          <Route path="/blog" element={<BlogPage />}/>
          <Route path="/blog/:slug" element={<BlogDetail />}/>
          <Route path="/product-test" element={<ProductTest />}/>
          <Route path="/contact" element={<Contact />}/>
          <Route path="/videos" element={<Videos />}/>
          <Route path="/pr" element={<Pr />} />
          <Route path="/more-ms" element={<MoreMS />} />
          <Route path="/product/:category/:productId" element={<ProductDetail />} />
          <Route path="/affiliate" element={<Affiliate/>}/>
          <Route path="/about-us" element={<Aboutus />} />
          {/* Platform Pages - All 17 Platforms */}
          <Route path="/platforms/google" element={<Google />} />
          <Route path="/platforms/youtube" element={<Youtube />} />
          <Route path="/platforms/facebook" element={<Facebook />} />
          <Route path="/platforms/wordpress" element={<Wordpress />} />
          <Route path="/platforms/instagram" element={<Instagram />} />
          <Route path="/platforms/tiktok" element={<TikTok />} />
          <Route path="/platforms/x" element={<XPlatform />} />
          <Route path="/platforms/linkedin" element={<LinkedIn />} />
          <Route path="/platforms/pinterest" element={<Pinterest />} />
          <Route path="/platforms/snapchat" element={<Snapchat />} />
          <Route path="/platforms/spotify" element={<Spotify />} />
          <Route path="/platforms/amazon" element={<Amazon />} />
          <Route path="/platforms/shopify" element={<ShopifyPlatform />} />
          <Route path="/platforms/wix" element={<WixPlatform />} />
          <Route path="/platforms/webflow" element={<Webflow />} />
          <Route path="/platforms/reddit" element={<Reddit />} />
          <Route path="/platforms/telegram" element={<Telegram />} />
          <Route path="/platforms/more" element={<MoreplatformOthers />} />
          <Route path="/social-media-post-generator" element={<SocialmedpostGenerator />} />
          <Route path="/digital-ad-generator" element={<DigitaladGenerator />} />
          <Route path="/logo-generator" element={<LogoGenerator />} />
          <Route path="/more-tools" element={<MoreTools />} />
          <Route path="/checkout" element={<Checkout />} />
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          {/* Support Pages */}
          <Route path="/help-center" element={<HelpCenter />} />
          {/* User Dashboard */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="customer">
              <PreviewGate title="Your dashboard is coming soon">
                <Dashboard />
              </PreviewGate>
            </ProtectedRoute>
          } />
          {/* Catch-all: unknown routes render a real 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
          </Suspense>
        <ConditionalFooter />
        <CookieConsentBanner />
          </div>
          </ErrorBoundary>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;